import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Trash2, Copy, Edit3, Flame, Beef, Wheat, Droplet, ChevronDown, ChevronUp, Mic, Save, Loader2, Droplets, Syringe, Activity } from 'lucide-react';
import { Skeleton } from '../../ui/skeleton';
import GlucoseSimulator from './GlucoseSimulator';

const MEAL_TIME_ICONS = {
  breakfast: '🌅',
  lunch: '☀️',
  dinner: '🌙',
  snack: '🍎',
};

const MEAL_TIME_COLORS = {
  breakfast: 'from-amber-500 to-orange-500',
  lunch: 'from-yellow-500 to-amber-500',
  dinner: 'from-indigo-500 to-purple-500',
  snack: 'from-pink-500 to-rose-500',
};

// Skeleton for food entries
function FoodEntrySkeleton({ count = 2 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="p-3 bg-slate-800/30 rounded-xl border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Skeleton className="h-4 w-32 mb-1 bg-slate-700" />
              <div className="flex gap-3 mt-1">
                <Skeleton className="h-3 w-14 bg-slate-800" />
                <Skeleton className="h-3 w-10 bg-slate-800" />
                <Skeleton className="h-3 w-10 bg-slate-800" />
                <Skeleton className="h-3 w-10 bg-slate-800" />
              </div>
            </div>
            <div className="flex gap-1">
              <Skeleton className="h-7 w-7 rounded bg-slate-700" />
              <Skeleton className="h-7 w-7 rounded bg-slate-700" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Single food entry component
function FoodEntry({ entry, onDeleteEntry, onCopyEntry, onEditEntry }) {
  return (
    <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm truncate">
            {entry.name}
          </p>
          {entry.grams && (
            <p className="text-slate-500 text-xs">{entry.grams}g</p>
          )}
        </div>
        <div className="flex items-center gap-1 ml-2">
          {onCopyEntry && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onCopyEntry(entry)}
              className="h-7 w-7 p-0 text-slate-400 hover:text-blue-400 hover:bg-blue-500/20"
              title="Copy to another day"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          )}
          {onEditEntry && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEditEntry(entry)}
              className="h-7 w-7 p-0 text-slate-400 hover:text-yellow-400 hover:bg-yellow-500/20"
              title="Edit"
            >
              <Edit3 className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDeleteEntry(entry.id)}
            className="h-7 w-7 p-0 text-red-400/70 hover:text-red-400 hover:bg-red-500/20"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      
      {/* Nutrition Info */}
      <div className="flex items-center gap-3 mt-2 text-xs">
        <span className="flex items-center gap-1 text-orange-400">
          <Flame className="h-3 w-3" />
          {entry.calories}
        </span>
        <span className="flex items-center gap-1 text-red-400">
          <Beef className="h-3 w-3" />
          {entry.protein}g
        </span>
        <span className="flex items-center gap-1 text-yellow-400">
          <Wheat className="h-3 w-3" />
          {entry.carbs}g
        </span>
        <span className="flex items-center gap-1 text-blue-400">
          <Droplet className="h-3 w-3" />
          {entry.fat}g
        </span>
      </div>
    </div>
  );
}

// Meal section component
function MealSection({ mealTime, entries, onDeleteEntry, onCopyEntry, onEditEntry, onCopyMeal, onSaveMeal, isExpanded, onToggle, bloodSugarData, onBloodSugarChange, diabetesEnabled, diabetesUnit, insulinEnabled }) {
  const [showSimulator, setShowSimulator] = useState(false);
  const icon = mealTime.icon || MEAL_TIME_ICONS[mealTime.id] || '🍽️';
  const colorClass = MEAL_TIME_COLORS[mealTime.id] || 'from-slate-500 to-slate-600';
  const totalCalories = entries.reduce((sum, e) => sum + (e.calories || 0), 0);
  const totalCarbs = entries.reduce((sum, e) => sum + (parseFloat(e.carbs) || 0), 0);
  const totalProtein = entries.reduce((sum, e) => sum + (parseFloat(e.protein) || 0), 0);
  const totalFat = entries.reduce((sum, e) => sum + (parseFloat(e.fat) || 0), 0);
  const totalFiber = entries.reduce((sum, e) => sum + (parseFloat(e.fiber) || 0), 0);
  const beforeEntry = bloodSugarData?.find(e => e.timing === 'before') || null;
  const afterEntry = bloodSugarData?.find(e => e.timing === 'after') || null;
  
  return (
    <div className="border border-slate-700/50 rounded-xl overflow-hidden">
      {/* Meal Header */}
      <button
        onClick={onToggle}
        className="w-full p-3 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
      >
        {/* Row 1: Icon + Name + Copy/Save + Chevron */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">{icon}</span>
            <span className="text-white font-medium text-sm">{mealTime.name}</span>
            <span className="text-slate-500 text-xs">
              ({entries.length})
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {onCopyMeal && entries.length > 0 && (
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); onCopyMeal(mealTime, entries); }}
                className="p-1 rounded-md bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                data-testid={`copy-meal-${mealTime.id}-btn`}
              >
                <Copy className="h-3 w-3" />
              </span>
            )}
            {onSaveMeal && entries.length > 0 && (
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); onSaveMeal(mealTime, entries); }}
                className="p-1 rounded-md bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
                data-testid={`save-meal-${mealTime.id}-btn`}
              >
                <Save className="h-3 w-3" />
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            )}
          </div>
        </div>
        {/* Row 2: Total macros */}
        {entries.length > 0 && (
          <div className="flex items-center gap-3 mt-1.5 ml-8 text-xs">
            <span className="text-orange-400 font-medium">{totalCalories} kcal</span>
            <span className="text-red-400">P: {Math.round(totalProtein)}g</span>
            <span className="text-yellow-400">C: {Math.round(totalCarbs)}g</span>
            <span className="text-blue-400">F: {Math.round(totalFat)}g</span>
          </div>
        )}
      </button>
      
      {/* Entries */}
      {isExpanded && (
        <div className="p-2 space-y-2 bg-slate-900/50">
          {/* Blood Sugar & Insulin Input - shown when Diabetes is ON */}
          {diabetesEnabled && (
            <div className="space-y-1.5" data-testid={`bs-input-row-${mealTime.id}`}>
              {/* Blood Sugar Row: Before meal | 2h After meal */}
              <div className="p-2 bg-rose-500/5 border border-rose-500/20 rounded-lg">
                <div className="flex items-center gap-2">
                  {/* Before meal */}
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <Droplets className="h-3.5 w-3.5 text-rose-400 shrink-0" />
                    <span className="text-[10px] text-rose-300 shrink-0">Before</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step={diabetesUnit === 'mmol/l' ? '0.1' : '1'}
                      maxLength={5}
                      placeholder="--"
                      value={beforeEntry?.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.length > 5) return;
                        if (onBloodSugarChange) {
                          onBloodSugarChange(mealTime.id, 'value', val === '' ? null : val, beforeEntry, 'before');
                        }
                      }}
                      onBlur={(e) => {
                        if (onBloodSugarChange) {
                          const val = e.target.value;
                          if (val) {
                            onBloodSugarChange(mealTime.id, 'save', parseFloat(val), beforeEntry, 'before');
                          } else if (beforeEntry?.entry_id) {
                            onBloodSugarChange(mealTime.id, 'delete_value', null, beforeEntry, 'before');
                          }
                        }
                      }}
                      className="w-14 bg-slate-800 border border-slate-600 rounded-md px-1.5 py-1 text-xs font-medium text-white placeholder-slate-500 focus:border-rose-400 focus:outline-none focus:ring-1 focus:ring-rose-400/30 text-center"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`bs-before-${mealTime.id}`}
                    />
                  </div>
                  {/* Separator */}
                  <span className="text-slate-700">|</span>
                  {/* 2h After meal */}
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <Droplets className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span className="text-[10px] text-amber-300 shrink-0">2h</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step={diabetesUnit === 'mmol/l' ? '0.1' : '1'}
                      maxLength={5}
                      placeholder="--"
                      value={afterEntry?.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.length > 5) return;
                        if (onBloodSugarChange) {
                          onBloodSugarChange(mealTime.id, 'value', val === '' ? null : val, afterEntry, 'after');
                        }
                      }}
                      onBlur={(e) => {
                        if (onBloodSugarChange) {
                          const val = e.target.value;
                          if (val) {
                            onBloodSugarChange(mealTime.id, 'save', parseFloat(val), afterEntry, 'after');
                          } else if (afterEntry?.entry_id) {
                            onBloodSugarChange(mealTime.id, 'delete_value', null, afterEntry, 'after');
                          }
                        }
                      }}
                      className="w-14 bg-slate-800 border border-slate-600 rounded-md px-1.5 py-1 text-xs font-medium text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400/30 text-center"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`bs-after-${mealTime.id}`}
                    />
                    <span className="text-[10px] font-medium text-slate-300 shrink-0">{diabetesUnit}</span>
                  </div>
                </div>
              </div>
              {/* Short Acting Insulin - separate box */}
              {insulinEnabled && (
                <div className="p-2 bg-sky-500/5 border border-sky-500/20 rounded-lg">
                  <div className="flex items-center gap-1.5">
                    <Syringe className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                    <span className="text-[10px] text-sky-300 shrink-0">Short insulin</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.5"
                      maxLength={4}
                      placeholder="--"
                      value={beforeEntry?.insulin_short ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.length > 4) return;
                        if (onBloodSugarChange) {
                          onBloodSugarChange(mealTime.id, 'insulin', val === '' ? null : val, beforeEntry, 'before');
                        }
                      }}
                      onBlur={(e) => {
                        if (onBloodSugarChange) {
                          const val = e.target.value;
                          if (val) {
                            onBloodSugarChange(mealTime.id, 'save_insulin', parseFloat(val), beforeEntry, 'before');
                          } else if (beforeEntry?.entry_id) {
                            onBloodSugarChange(mealTime.id, 'delete_insulin', null, beforeEntry, 'before');
                          }
                        }
                      }}
                      className="w-14 bg-slate-800 border border-slate-600 rounded-md px-1.5 py-1 text-xs font-medium text-white placeholder-slate-500 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400/30 text-center"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`bs-insulin-${mealTime.id}`}
                    />
                    <span className="text-[10px] font-medium text-slate-300 shrink-0">u</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Food Entries */}
          {entries.length > 0 ? (
            <>
              {entries.map((entry) => (
                <FoodEntry
                  key={entry.id}
                  entry={entry}
                  onDeleteEntry={onDeleteEntry}
                  onCopyEntry={onCopyEntry}
                  onEditEntry={onEditEntry}
                />
              ))}
            </>
          ) : (
            <p className="text-slate-500 text-xs text-center py-3">
              No food added to {mealTime.name.toLowerCase()}
            </p>
          )}

          {/* Glucose Impact Simulator - after food entries and insulin */}
          {diabetesEnabled && entries.length > 0 && totalCarbs > 0 && (
            showSimulator ? (
              <GlucoseSimulator
                meal={{
                  name: `${mealTime.name} (${entries.length} items)`,
                  calories: totalCalories,
                  carbs: totalCarbs,
                  protein: totalProtein,
                  fat: totalFat,
                  fiber: totalFiber,
                  mealTime: mealTime.id,
                  insulinDose: parseFloat(beforeEntry?.insulin_short) || null,
                }}
                onClose={() => setShowSimulator(false)}
              />
            ) : (
              <button
                onClick={() => setShowSimulator(true)}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/20 transition-colors text-xs"
                data-testid={`simulate-meal-${mealTime.id}`}
              >
                <Activity className="h-3.5 w-3.5" />
                Simulate glucose ({Math.round(totalCarbs)}g carbs)
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

export function FoodEntryList({
  entries,
  mealTimes,
  selectedMealTime,
  onSelectMealTime,
  onDeleteEntry,
  onCopyEntry,
  onEditEntry,
  onVoiceFood,
  voiceFoodRecording,
  voiceFoodProcessing,
  voiceLanguage,
  onVoiceLanguageChange,
  voiceLanguages,
  onCopyDay,
  onSaveToMeals,
  onCopyMeal,
  onSaveMeal,
  isLoading = false,
  bloodSugarByMeal = {},
  onBloodSugarChange,
  diabetesEnabled = false,
  diabetesUnit = 'mmol/l',
  insulinEnabled = false,
}) {
  const [expandedMeals, setExpandedMeals] = React.useState(() => {
    // Initially expand all meals that have entries
    const expanded = {};
    mealTimes.forEach(mt => {
      expanded[mt.id] = true;
    });
    return expanded;
  });

  // Group entries by meal time
  const entriesByMealTime = React.useMemo(() => {
    const grouped = {};
    mealTimes.forEach(mt => {
      grouped[mt.id] = (entries || []).filter(e => e.mealTime === mt.id);
    });
    return grouped;
  }, [entries, mealTimes]);

  const allEntries = entries || [];
  const totalCalories = allEntries.reduce((sum, e) => sum + (e.calories || 0), 0);

  const toggleMeal = (mealId) => {
    setExpandedMeals(prev => ({
      ...prev,
      [mealId]: !prev[mealId]
    }));
  };

  return (
    <div className="space-y-4">
      {/* Today's Food Card */}
      <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>🍽️</span>
              Today's food
            </div>
            <div className="flex items-center gap-1">
              {/* Voice language selector */}
              {voiceLanguages && voiceLanguages.length > 0 && (
                <select
                  value={voiceLanguage}
                  onChange={(e) => onVoiceLanguageChange(e.target.value)}
                  className="bg-slate-700 text-slate-300 text-[10px] rounded px-1 py-0.5 border-0 outline-none h-6"
                  data-testid="voice-language-select"
                >
                  {voiceLanguages.map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              )}
              {/* Voice food dictation */}
              {onVoiceFood && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onVoiceFood}
                  disabled={voiceFoodProcessing}
                  className={`h-7 w-7 p-0 ${
                    voiceFoodRecording 
                      ? 'text-red-400 bg-red-500/30 hover:bg-red-500/40 hover:text-red-300 animate-pulse' 
                      : 'text-violet-400 bg-violet-500/30 hover:bg-violet-500/40 hover:text-violet-300'
                  }`}
                  title={voiceFoodRecording ? 'Stop recording' : voiceFoodProcessing ? 'Processing...' : 'Voice food logging'}
                  data-testid="voice-food-btn"
                >
                  {voiceFoodProcessing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : voiceFoodRecording ? (
                    <span className="h-3 w-3 rounded-sm bg-red-400" />
                  ) : (
                    <Mic className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
              {/* Copy day - light blue */}
              {onCopyDay && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCopyDay}
                  className="h-7 w-7 p-0 text-blue-400 bg-blue-500/30 hover:bg-blue-500/40 hover:text-blue-300"
                  title="Copy day to another date"
                  data-testid="copy-day-btn"
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              )}
              {/* Save to meals - orange */}
              {onSaveToMeals && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onSaveToMeals}
                  className="h-7 w-7 p-0 text-amber-400 bg-amber-500/30 hover:bg-amber-500/40 hover:text-amber-300"
                  title="Save as meal template"
                  data-testid="save-to-meals-btn"
                >
                  <Save className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-2">
          {mealTimes.length > 0 ? (
            mealTimes.map((mealTime) => (
              <MealSection
                key={mealTime.id}
                mealTime={mealTime}
                entries={entriesByMealTime[mealTime.id] || []}
                onDeleteEntry={onDeleteEntry}
                onCopyEntry={onCopyEntry}
                onEditEntry={onEditEntry}
                onCopyMeal={onCopyMeal}
                onSaveMeal={onSaveMeal}
                isExpanded={expandedMeals[mealTime.id]}
                onToggle={() => toggleMeal(mealTime.id)}
                bloodSugarData={bloodSugarByMeal[mealTime.id]}
                onBloodSugarChange={onBloodSugarChange}
                diabetesEnabled={diabetesEnabled}
                diabetesUnit={diabetesUnit}
                insulinEnabled={insulinEnabled}
              />
            ))
          ) : (
            <div className="text-center py-6">
              <p className="text-slate-500 text-sm">No mealtimes configured</p>
              <p className="text-slate-600 text-xs mt-1">
                Go to Personal → Mealtimes to set up your meals
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default FoodEntryList;
